import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MoveHorizontal } from 'lucide-react';

const BeforeAfterSlider = ({ beforeImage, afterImage }) => {
    const [sliderPosition, setSliderPosition] = useState(50);
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef(null);

    const handleMove = useCallback((clientX) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
        const percentage = (x / rect.width) * 100;
        setSliderPosition(percentage);
    }, []);

    const onMouseDown = (e) => {
        setIsDragging(true);
        handleMove(e.clientX);
    };

    const onTouchStart = (e) => {
        setIsDragging(true);
        handleMove(e.touches[0].clientX);
    };

    useEffect(() => {
        const onMouseMove = (e) => {
            if (isDragging) handleMove(e.clientX);
        };
        const onMouseUp = () => setIsDragging(false);
        const onTouchMove = (e) => {
            if (isDragging) handleMove(e.touches[0].clientX);
        };

        if (isDragging) {
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
            window.addEventListener('touchmove', onTouchMove, { passive: false });
            window.addEventListener('touchend', onMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
            window.removeEventListener('touchmove', onTouchMove);
            window.removeEventListener('touchend', onMouseUp);
        };
    }, [isDragging, handleMove]);

    return (
        <div
            ref={containerRef}
            className="relative w-full h-full overflow-hidden select-none cursor-ew-resize group"
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
        >
            {/* After Image (Full background) */}
            <img
                src={afterImage}
                alt="Sonrası"
                className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            />

            {/* Before Image (Clipped) */}
            <img
                src={beforeImage}
                alt="Öncesi"
                className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                style={{ clipPath: `polygon(0 0, ${sliderPosition}% 0, ${sliderPosition}% 100%, 0 100%)` }}
            />

            {/* Labels */}
            <div className={`absolute top-4 left-4 px-3 py-1 bg-black/50 text-white text-[10px] font-bold tracking-widest uppercase rounded-full backdrop-blur-md transition-opacity duration-300 ${sliderPosition < 10 ? 'opacity-0' : 'opacity-100'}`}>
                Öncesi
            </div>
            <div className={`absolute top-4 right-4 px-3 py-1 bg-black/50 border border-white/20 text-white text-[10px] font-bold tracking-widest uppercase rounded-full backdrop-blur-md transition-opacity duration-300 ${sliderPosition > 90 ? 'opacity-0' : 'opacity-100'}`}>
                Sonrası
            </div>

            {/* Slider Handle */}
            <div
                className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize shadow-[0_0_10px_rgba(0,0,0,0.5)] z-20 flex items-center justify-center pointer-events-none"
                style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
            >
                <div className={`w-8 h-8 md:w-10 md:h-10 bg-white rounded-full shadow-xl flex items-center justify-center text-slate-900 transition-transform ${isDragging ? 'scale-90' : 'scale-100'}`}>
                    <MoveHorizontal size={16} />
                </div>
            </div>
        </div>
    );
};

export default BeforeAfterSlider;
